type SectionElementLayoutTypes = 'overview' | 'one-column' | 'two-rows' | 'two-columns';

export interface BaseElementType {
	attributes?: Record<string, string | undefined>;
	children?: Array<ParsedElementType> | string;
	tag: string;
}

// Section element with layout attributes
export interface SectionElementType extends BaseElementType {
	attributes?: {
		[key: string]: string | undefined;
		folded?: '1' | undefined;
		layout: SectionElementLayoutTypes;
		n: string;
	};
	tag: 'section' | string;
}

// Markdown content element
export interface MarkdownElementType extends BaseElementType {
	attributes?: {
		[key: string]: string | undefined;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
	tag: 'md';
}

// Header element
export interface HeaderElementType extends BaseElementType {
	attributes?: {
		[key: string]: string;
		header: string;
		level: '1' | '2' | '3' | '4' | '5' | '6' | string;
	};
	tag: 'h';
}

export function isHeaderElement(element: ParsedElementType): element is HeaderElementType {
	return typeof element !== 'string' && element.tag === 'h';
}

// Carousel element for displaying multiple items
export interface CarouselElementType extends BaseElementType {
	attributes?: {
		[key: string]: string | undefined;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
		title: string;
	};
	tag: 'carousel';
}

// Smart link element
export interface SmartLinkElementType extends BaseElementType {
	attributes?: {
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
		title?: string;
		type?: string;
		url?: string;
	};
	tag: 'smart-link';
}

// Action element
export interface ActionElementType extends BaseElementType {
	attributes?: {
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
		text?: string;
		title: string;
		url?: string;
	};
	tag: 'action-card';
}

// Action element
export interface ProjectHighlightElementType extends BaseElementType {
	attributes?: {
		content?: string;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
		title: string;
	};
	tag: 'project-highlight-card';
}

// Contact element
export interface ContactElementType extends BaseElementType {
	attributes?: {
		avatar_url?: string;
		job_title?: string;
		message?: string;
		name?: string;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
	tag: 'contact-card';
}

// People element
export interface PersonElementType extends BaseElementType {
	attributes?: {
		[key: string]: string | undefined;
		account_id?: string;
		avatar_url?: string;
		email?: string;
		job_title?: string;
		location?: string;
		name?: string;
		url?: string;
	};
	tag: 'people-card';
}

// Reporting lines element
export interface ReportingLinesElementType extends BaseElementType {
	attributes?: {
		id?: string;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
	tag: 'report-lines';
}

// List element
export interface ListElementType extends BaseElementType {
	attributes?: {
		[key: string]: string | undefined;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
		type?: string;
	};
	tag: 'ul' | 'ol' | 'list';
}

// List item element
export interface ListItemElementType extends BaseElementType {
	attributes?: {
		[key: string]: string | undefined;
	};
	tag: 'li';
}

// Jira epic progress element (epics, epic issues)
export interface JiraEpicProgressElementType extends BaseElementType {
	attributes?: {
		id?: string;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
	tag: 'jira-epic-progress';
}

// Jira project burndown chart element
export interface JiraBurndownChartElementType extends BaseElementType {
	attributes?: {
		id?: string;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
	tag: 'jira-burndown-chart';
}

// Jira Work Distribution element
export interface JiraWorkDistributionElementType extends BaseElementType {
	attributes?: {
		id?: string;
		pos?: 'top' | 'bottom' | 'left' | 'right' | string;
	};
	tag: 'jira-work-distribution';
}

// Union type of all possible parsed elements
export type ParsedElementType =
	| SectionElementType
	| ListElementType
	| MarkdownElementType
	| HeaderElementType
	| CarouselElementType
	| SmartLinkElementType
	| ActionElementType
	| PersonElementType
	| ProjectHighlightElementType
	| ContactElementType
	| ReportingLinesElementType
	| JiraEpicProgressElementType
	| JiraWorkDistributionElementType
	| JiraBurndownChartElementType
	| string;

export type ParsedStructuredElementType = Exclude<ParsedElementType, string>;

export type ParsedLayout = SectionElementType[];
